// src/api/v3/taxonomy-api-v3.js
// Robust CRUD helpers for taxonomy endpoints with flexible query input.
//
// Usage examples:
//   BrandsAPI.list({ page: 1, per: 25, q: 'her' })
//   BrandsAPI.list('?page=1&per=25&q=her')
//   BrandsAPI.create({ name: 'Hermes', code: 'HE', active: true })

const API_BASE   = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/,'');
const API_PREFIX = (process.env.REACT_APP_API_PREFIX || '/api/v3').replace(/\/+$/,'');
const B = (p = '') => `${API_BASE}${API_PREFIX}${p}`;

// Convert an object or string into a query string.
// - Object: {page:1, per:25, q:'x'}  -> "?page=1&per=25&q=x"
// - String: "page=1&per=25"          -> "?page=1&per=25"
// - String starting with "?": passthrough
function toQuery(q) {
  if (!q) return '';
  if (typeof q === 'string') return q.startsWith('?') ? q : `?${q}`;
  const sp = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (v == null || v === '') return;
    if (Array.isArray(v)) v.forEach(val => sp.append(k, String(val)));
    else sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function jsonFetch(path, { method = 'GET', body, headers = {}, ...rest } = {}) {
  const url = B(path);
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    ...rest,
  });

  const contentType = res.headers.get('content-type') || '';
  const tryJson = async () => (contentType.includes('application/json') ? res.json() : null);

  if (!res.ok) {
    // Try to extract a meaningful error message
    let msg = `API ${method} ${path} failed: ${res.status}`;
    try {
      const data = await tryJson();
      if (data) {
        const errors =
          data.error ||
          (Array.isArray(data.errors) ? data.errors.join(', ') : data.errors) ||
          data.message;
        if (errors) msg = `${msg} - ${errors}`;
      } else {
        const text = await res.text();
        if (text) msg = `${msg} - ${text}`;
      }
    } catch (_) {
      // ignore parse errors
    }
    throw new Error(msg);
  }

  return tryJson();
}

// Factory for simple CRUD APIs at /<resource>
const makeCRUD = (resource) => ({
  list:   (q = '')          => jsonFetch(`/${resource}${toQuery(q)}`),
  show:   (id)              => jsonFetch(`/${resource}/${id}`),
  create: (payload)         => jsonFetch(`/${resource}`,       { method: 'POST', body: payload }),
  update: (id, payload)     => jsonFetch(`/${resource}/${id}`, { method: 'PUT',  body: payload }),
  destroy:(id)              => jsonFetch(`/${resource}/${id}`, { method: 'DELETE' }),
});

// ---- Concrete APIs ---------------------------------------------------------
export const BrandsAPI          = makeCRUD('brands');
export const CategoriesAPI      = makeCRUD('categories');
export const ColoursAPI         = makeCRUD('colours');
export const MaterialsAPI       = makeCRUD('materials');     // ✅ new
export const SizesAPI           = makeCRUD('sizes');         // ✅ new
export const OrderCategoriesAPI = makeCRUD('order_categories');

export default {
  BrandsAPI,
  CategoriesAPI,
  ColoursAPI,
  MaterialsAPI,
  SizesAPI,
  OrderCategoriesAPI,
};
